// src/services/axiosConfig.js
import axios from 'axios';
import config from '../config';
import { message } from 'antd';
import { store, persistor } from '../store/auth/store';
import { clearLogout, updateToken } from '../store/auth/authSlice';

// ————————————————————————————————————————————
// 1) Defaults
axios.defaults.baseURL = config.baseUrl;
axios.defaults.withCredentials = true;

// 2) Rutas que NO llevan token
const isAuthRoute = (url = '') =>
    url.endsWith('/api/auth/login') ||
    url.endsWith('/api/auth/refresh') ||
    url.endsWith('/api/auth/logout');   // <-- añadimos el logout aquí

const RENEW_ENDPOINT = '/api/auth/renew-token';

// 3) Renovación silenciosa: si varias peticiones expiran a la vez, todas
// comparten la misma llamada de renovación en vez de disparar N renovaciones.
let renewalInFlight = null;
const renewToken = () => {
    if (!renewalInFlight) {
        renewalInFlight = axios
            .post(RENEW_ENDPOINT)
            .then(({ data }) => {
                store.dispatch(updateToken(data.token));
                return data.token;
            })
            .finally(() => {
                renewalInFlight = null;
            });
    }
    return renewalInFlight;
};

const forceLogout = async () => {
    message.warning('Tu sesión ha expirado o fue cerrada en otro dispositivo.', 3);
    // limpia cookie backend
    try { await axios.post('/api/auth/logout'); } catch (_) { }

    // limpia estado
    await persistor.purge();
    store.dispatch(clearLogout());
    localStorage.clear();

    // notificamos al modal sin recargar la página
    window.dispatchEvent(new Event('force-logout'));
};

// 4) Request interceptor: añade el Bearer token
axios.interceptors.request.use(cfg => {
    if (!isAuthRoute(cfg.url)) {
        const token = store.getState().auth.user?.token;

        if (token) cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
}, err => Promise.reject(err));

// 5) Response interceptor: ante un 401 intenta renovar el token una vez;
// si la renovación también falla, fuerza logout.
axios.interceptors.response.use(
    res => res,
    async err => {
        const status = err.response?.status;
        const url = err.config?.url || '';
        const hasAuth = Boolean(err.config?.headers?.Authorization);
        const isRenewCall = url.endsWith(RENEW_ENDPOINT);

        if (status === 401 && !isAuthRoute(url) && hasAuth) {
            if (!isRenewCall && !err.config._retriedAfterRenew) {
                try {
                    const newToken = await renewToken();
                    err.config._retriedAfterRenew = true;
                    err.config.headers.Authorization = `Bearer ${newToken}`;
                    return axios(err.config);
                } catch (_) {
                    // la renovación falló: la sesión ya no es recuperable, se cae al logout
                }
            }
            await forceLogout();
        }
        return Promise.reject(err);
    }
);

// 6) Para que cualquier axios.create herede los mismos interceptores
const originalCreate = axios.create.bind(axios);
axios.create = cfg => {
    const instance = originalCreate(cfg);
    // copiar defaults
    instance.defaults.baseURL = axios.defaults.baseURL;
    instance.defaults.withCredentials = axios.defaults.withCredentials;
    // copiar interceptores request
    axios.interceptors.request.handlers.forEach(h =>
        instance.interceptors.request.use(h.fulfilled, h.rejected)
    );
    // copiar interceptores response
    axios.interceptors.response.handlers.forEach(h =>
        instance.interceptors.response.use(h.fulfilled, h.rejected)
    );
    return instance;
};
